import React from 'react';
import { Lock, CreditCard } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useOverlayOpen } from '@/lib/overlay';
import { usePurchaseBarLock } from '@/lib/purchaseBar';
import { money, LEAD_PRICE } from '@/data/constants';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * MOBILE LEAD PURCHASE BAR — the $29 CTA, always on screen.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * WHY THIS EXISTS (the revenue bug it fixes)
 *
 * The lead detail page is `grid lg:grid-cols-[1fr_340px]`: description column
 * first, purchase card second. Above 1024px both columns are side by side and
 * the card is `sticky top-24`, so the pay button is permanently visible.
 *
 * BELOW 1024px — every phone, and portrait tablets — the grid collapses to a
 * single column and that second cell renders AFTER the whole first cell:
 * photo gallery, three detail tiles, the locked-location notice, the full
 * description, the document list and up to three "similar projects" cards.
 * The pay button ended up roughly 2,500–3,500 px down the page, and
 * `sticky top-24` cannot rescue it because the element is already the last
 * thing in the document — there is nothing left to scroll past. Nothing was
 * hidden by CSS, disabled, or role-gated: the button simply was not on screen
 * for a mobile customer who did not scroll to the very bottom.
 *
 * This bar removes the dependency on scrolling entirely. It is pinned to the
 * bottom of the viewport on every screen below `lg`, states the price, and its
 * single button runs the SAME handler as the sidebar button, so there is one
 * code path and one server-side check — no duplicated purchase logic.
 *
 * Mobile correctness details
 *  • `pb-[env(safe-area-inset-bottom)]` keeps it clear of the iOS home
 *    indicator and Android gesture bar.
 *  • 52px minimum touch target, `touch-action: manipulation` so iOS fires the
 *    tap immediately instead of waiting for a possible double-tap zoom.
 *  • `z-[95]` — below the checkout modal (z-100) and the plans modal (z-130),
 *    and it hides itself outright while any overlay is open, so it can never
 *    cover a dialog or be tapped through one.
 *  • It registers in the purchase-bar registry, which makes the floating
 *    "Get Leads Now" pill stand down and lifts WhatsApp above it, so nothing
 *    overlaps the pay button.
 *  • Text is never truncated at 320px: the price sits on its own line and the
 *    label is the short, explicit action.
 */

interface Props {
  /** True once the viewer owns this lead — the bar is unnecessary. */
  unlocked: boolean;
  /** True when 6 of 6 contractor slots are taken — nothing is purchasable. */
  soldOut: boolean;
  /** Spendable credits. > 0 renders the credit label instead of the price. */
  credits: number;
  /** Same handler as the sidebar CTA: login redirect → role check → checkout. */
  onPurchase: () => void;
}

const LeadPurchaseBar: React.FC<Props> = ({ unlocked, soldOut, credits, onPurchase }) => {
  const { t } = useI18n();
  const overlayOpen = useOverlayOpen();
  const show = !unlocked && !soldOut;

  // Tell the floating stack to get out of the way while the bar is on screen.
  usePurchaseBarLock(show);

  if (!show) return null;

  const hasCredit = credits > 0;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-[95] border-t border-slate-200 bg-white/97 backdrop-blur transition-opacity duration-200 lg:hidden ${
        overlayOpen ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.12)',
      }}
      aria-hidden={overlayOpen}
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
        <div className="min-w-0 shrink-0">
          <p className="text-[10px] font-bold uppercase leading-tight tracking-wide text-slate-400">
            {hasCredit ? t('leads.remaining') : t('jobs.leadPrice')}
          </p>
          <p className="text-xl font-extrabold leading-tight text-slate-900">
            {hasCredit ? credits : money(LEAD_PRICE)}
          </p>

        </div>

        <button
          type="button"
          onClick={onPurchase}
          tabIndex={overlayOpen ? -1 : 0}
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
          className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-xl bg-orange-500 px-3 py-3 text-[15px] font-bold text-white transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
        >
          {hasCredit ? <Lock className="h-4 w-4 shrink-0" /> : <CreditCard className="h-4 w-4 shrink-0" />}
          <span className="truncate">
            {hasCredit ? t('jobs.unlockWithCredit') : `${t('pay.buyThisLead')} — ${money(LEAD_PRICE)}`}
          </span>
        </button>
      </div>
    </div>
  );
};

export default LeadPurchaseBar;
